/**
 * Browser-safe MFA shapes for the pinned GoTrue factor API (#1644).
 *
 * GoTrue owns factor custody.  In particular, the TOTP secret exists only in
 * the response to enrolment; it is never part of Session and is never written
 * through SessionStorageAdapter.  The small reveal/clear envelope also keeps
 * accidental JSON serialization from turning an enrolment frame into durable
 * browser state.
 */

export type MfaFactorType = 'totp' | 'phone' | 'webauthn' | (string & {})
export type MfaFactorStatus = 'verified' | 'unverified' | (string & {})

export interface MfaFactor {
  id: string
  type: MfaFactorType
  status: MfaFactorStatus
  friendlyName: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface TotpEnrollmentMaterial {
  /** Raw SVG supplied by GoTrue. Render it as an image; never inject it as HTML. */
  qrCode: string
  /** Manual-entry alternative. Present only for this enrolment ceremony. */
  secret: string
  uri: string
}

export interface TotpEnrollment {
  factor: MfaFactor
  /** Returns a copy until clear() is called, then null. */
  reveal(): TotpEnrollmentMaterial | null
  /** Drop the package's last reference when the enrolment frame closes. */
  clear(): void
}

type RawFactor = {
  id?: unknown
  factor_type?: unknown
  type?: unknown
  status?: unknown
  friendly_name?: unknown
  created_at?: unknown
  updated_at?: unknown
}

const optionalText = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

export function readFactor(value: unknown, fallbackStatus: MfaFactorStatus = 'unverified'): MfaFactor | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as RawFactor
  const id = optionalText(raw.id)
  const type = optionalText(raw.factor_type) ?? optionalText(raw.type)
  if (!id || !type) return null
  return Object.freeze({
    id,
    type,
    status: optionalText(raw.status) ?? fallbackStatus,
    friendlyName: optionalText(raw.friendly_name),
    createdAt: optionalText(raw.created_at),
    updatedAt: optionalText(raw.updated_at),
  })
}

/**
 * GET /user is the pinned server's caller-owned factor list.
 *
 * A missing factors member means the caller has none.  A present but malformed
 * member is a broken security response, not an empty list: returning a partial
 * list could make the UI claim that a factor does not exist.
 */
export function readFactorsFromUser(value: unknown): MfaFactor[] | null {
  if (!value || typeof value !== 'object') return null
  const factors = (value as { factors?: unknown }).factors
  if (factors === undefined) return []
  if (!Array.isArray(factors)) return null
  const result = factors.map((factor) => readFactor(factor))
  return result.some((factor) => factor == null) ? null : result as MfaFactor[]
}

export function readTotpEnrollment(value: unknown): TotpEnrollment | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as RawFactor & { totp?: unknown }
  const factor = readFactor(raw)
  if (!factor || factor.type !== 'totp' || !raw.totp || typeof raw.totp !== 'object') return null
  const totp = raw.totp as { qr_code?: unknown; secret?: unknown; uri?: unknown }
  const qrCode = optionalText(totp.qr_code)
  const secret = optionalText(totp.secret)
  const uri = optionalText(totp.uri)
  if (!qrCode || !secret || !uri) return null

  let material: TotpEnrollmentMaterial | null = { qrCode, secret, uri }
  return Object.freeze({
    factor,
    reveal: () => material ? { ...material } : null,
    clear: () => { material = null },
  })
}
